// src/version.ts
var SIM_VERSION = 2;

// src/types.ts
var MODULE_TYPES = [
  "core",
  "dock",
  "solar_panel",
  "battery",
  "o2_recycler",
  "habitat",
  "cantina",
  "repair_workshop",
  "fuel_depot",
  "warehouse",
  "maintenance_bay"
];
var NEED_KINDS = ["fuel", "repair", "rest", "trade"];
var FAILURE_KINDS = ["air_leak", "overheat", "short_circuit"];
var SHIP_CLASSES = ["shuttle", "freighter", "liner"];

// src/errors.ts
var SimError = class extends Error {
  code;
  details;
  constructor(code, message, details) {
    super(message);
    this.name = "SimError";
    this.code = code;
    this.details = details;
  }
};
var ReplayError = class extends SimError {
  actionIndex;
  constructor(actionIndex, cause) {
    super(cause.code, `Action n\xB0${actionIndex} refus\xE9e : ${cause.message}`, { ...cause.details, actionIndex });
    this.name = "ReplayError";
    this.actionIndex = actionIndex;
  }
};
function isSimError(error) {
  if (error instanceof SimError) return true;
  return typeof error === "object" && error !== null && "code" in error && "name" in error && (error.name === "SimError" || error.name === "ReplayError");
}
function isReplayError(error) {
  return isSimError(error) && error.name === "ReplayError" && "actionIndex" in error;
}
function fail(code, message, details) {
  throw new SimError(code, message, details);
}

// src/config/balance.ts
var module = (values) => ({
  power: 0,
  crew: 0,
  crewCapacity: 0,
  oxygen: 0,
  oxygenTank: 0,
  battery: 0,
  berths: 0,
  teams: 0,
  service: 0,
  wear: 0,
  ...values
});
var BALANCE = {
  start: {
    credits: 1200,
    reputation: 2500,
    morale: 8e3
  },
  grid: {
    /** Dernier anneau constructible : la station tient dans une grille de 11 × 11. */
    maxRing: 5,
    /** Anneaux ouverts au départ. */
    startRings: 3,
    /** Premier anneau avec gravité : en dessous, apesanteur (docks, industrie). */
    gravityFromRing: 3,
    /** Prix d'ouverture de chaque anneau (indice = anneau). */
    ringCost: [0, 0, 0, 0, 4e3, 12e3]
  },
  /** Chaque module du même type déjà construit renchérit le suivant (dix-millièmes du prix de base). */
  costGrowthPerOwned: 2500,
  /**
   * Usure de départ : la station a déjà servi, et son dock approche du seuil des pannes. La première
   * panne arrive dans la première heure, quand il reste peu de choses à gérer.
   */
  startWear: { dock: 58e4, solar_panel: 3e5 },
  /** Orbite de 6 minutes, dont 2 dans l'ombre de la planète. */
  orbit: {
    periodSeconds: 360,
    shadowSeconds: 120
  },
  modules: {
    // Plancher : le noyau ne tombe jamais en panne et garde un générateur, de l'oxygène, un équipage
    // minimal, une équipe de maintenance et un sas qui accueille de petites navettes de commerce.
    core: module({
      cost: 0,
      buildSeconds: 0,
      power: 6,
      crewCapacity: 4,
      oxygen: 800,
      oxygenTank: 3e4,
      battery: 150,
      berths: 1,
      teams: 1,
      service: 50
    }),
    dock: module({ cost: 150, buildSeconds: 20, power: -2, crew: 1, berths: 1, wear: 20 }),
    solar_panel: module({ cost: 80, buildSeconds: 10, power: 8, wear: 25 }),
    battery: module({ cost: 100, buildSeconds: 15, battery: 600, wear: 20 }),
    o2_recycler: module({ cost: 120, buildSeconds: 20, power: -3, crew: 1, oxygen: 800, oxygenTank: 2e4, wear: 35 }),
    habitat: module({ cost: 140, buildSeconds: 25, power: -2, crewCapacity: 4, wear: 20 }),
    cantina: module({ cost: 160, buildSeconds: 25, power: -3, crew: 1, service: 100, wear: 20 }),
    repair_workshop: module({ cost: 180, buildSeconds: 30, power: -4, crew: 2, service: 60, wear: 26 }),
    fuel_depot: module({ cost: 160, buildSeconds: 25, power: -2, crew: 1, service: 100, wear: 26 }),
    warehouse: module({ cost: 140, buildSeconds: 20, power: -1, crew: 1, service: 100, wear: 16 }),
    maintenance_bay: module({ cost: 200, buildSeconds: 30, power: -2, crew: 2, teams: 1, wear: 25 })
  },
  oxygen: {
    /** Consommation par membre d'équipage et par tick. */
    perCrew: 100,
    /** Perte par fuite d'air contaminante (palier 2 et plus), par tick. */
    airLeakLoss: 200
  },
  morale: {
    base: 7e3,
    /** Une cantina en service remonte le moral de son anneau. */
    cantinaBonus: 1500,
    /** Chaque fuite d'air contaminante dans l'anneau. */
    airLeakPenalty: 2500,
    /** Réserve d'oxygène vide. */
    oxygenShortagePenalty: 3e3,
    /** Vitesse à laquelle le moral rejoint sa cible, par tick. */
    driftPerTick: 20,
    /** En dessous de ce moral, le rendement des modules de l'anneau baisse… */
    fullEfficiencyFrom: 6e3,
    /** … jusqu'à ce rendement, à moral nul. */
    minEfficiency: 6e3
  },
  ships: {
    /**
     * L'autoroute spatiale passe devant la station sans jamais s'arrêter : dès qu'un poste d'amarrage est
     * libre, un vaisseau la quitte pour lui. Délai avant qu'il sorte de l'autoroute, tiré entre `exitMin` et
     * un maximum qui raccourcit avec la réputation (de `exitMaxAtZero` à `exitMaxAtFull`).
     */
    highway: {
      exitMin: 2,
      exitMaxAtZero: 8,
      exitMaxAtFull: 3
    },
    approachSeconds: 8,
    leaveSeconds: 6,
    /** Une escale dure au moins ce temps, même servie d'un coup : chaque passage se voit. */
    minDockSeconds: 20,
    /**
     * Débit d'un poste d'amarrage : un vaisseau à quai reçoit au plus ceci par tick et par besoin
     * (centièmes d'unité), même quand les modules de service ont de la réserve. Il règle la durée d'une
     * escale : environ 25 s pour une navette, 50 pour un cargo, 90 pour un long-courrier.
     */
    transferPerTick: 100,
    classes: {
      shuttle: {
        weight: 6e3,
        weightPerReputation: -4500,
        minNeeds: 1,
        maxNeeds: 2,
        amount: 25,
        patienceSeconds: 150,
        crewOxygen: 100,
        priceFactor: 1e4,
        reputationGain: 6,
        reputationLoss: 20,
        reputationLossEmpty: 30
      },
      freighter: {
        weight: 3e3,
        weightPerReputation: 1e3,
        minNeeds: 2,
        maxNeeds: 3,
        amount: 50,
        patienceSeconds: 200,
        crewOxygen: 200,
        priceFactor: 12e3,
        reputationGain: 12,
        reputationLoss: 30,
        reputationLossEmpty: 45
      },
      liner: {
        weight: 500,
        weightPerReputation: 5e3,
        minNeeds: 3,
        maxNeeds: 4,
        amount: 90,
        patienceSeconds: 260,
        crewOxygen: 400,
        priceFactor: 16e3,
        reputationGain: 25,
        reputationLoss: 50,
        reputationLossEmpty: 75
      }
    },
    /** Prix payé par unité servie, en centièmes de crédit. */
    prices: { fuel: 11, repair: 16, rest: 11, trade: 11 },
    /** Pourboire d'un vaisseau entièrement servi, en dix-millièmes du paiement. */
    tip: 2500,
    /** Une station réputée se paie plus cher : paiement en plus à réputation pleine (dix-millièmes). */
    reputationBonus: 3e3
  },
  reputation: {
    /** Plancher : la station attire toujours un peu de trafic. */
    floor: 1e3,
    max: 1e4,
    /** Toutes les N secondes, chaque panne retire des points selon son palier (indice = palier). */
    failurePenaltyEverySeconds: 10,
    failurePenalty: [0, 0, 1, 2]
  },
  wear: {
    max: 1e6,
    /**
     * Seuils d'affichage : usé (une révision rapporte de l'expérience), puis critique, au seuil des pannes :
     * un module critique peut lâcher à tout moment, on le voit venir.
     */
    worn: 5e5,
    critical: 6e5,
    /** En dessous, aucune panne possible. */
    failureFrom: 6e5,
    /** Probabilité de panne par tick à usure maximale (millionièmes), croissante depuis `failureFrom`. */
    failureChancePpm: 1e4,
    /** Pannes plus probables dans l'ombre (dix-millièmes). */
    nightFactor: 15e3,
    /** Chaque rustine rend la panne suivante plus probable (dix-millièmes en plus). */
    fragilityPerPatch: 5e3,
    /** Un module de service sans client s'use quand même un peu (part de l'usure pleine). */
    idleLoad: 5e3,
    /** Usure infligée par une surchauffe contaminante à chaque voisin, par tick. */
    overheatNeighbor: 150,
    /** Robustesse de chaque module, tirée à sa construction (multiplicateur d'usure, dix-millièmes). */
    factorRange: { min: 7e3, max: 14e3 }
  },
  failures: {
    /** Durée avant de passer au palier suivant sans intervention (indice = palier actuel). */
    stageSeconds: [0, 180, 300],
    /** Rendement du module selon le palier (indice = palier, 0 : pas de panne). */
    efficiency: [1e4, 6e3, 4e3, 0],
    /** Ce que chaque panne retire à l'état de la station (trafic, qualité des vaisseaux). */
    condition: [0, 300, 700, 1500],
    conditionFloor: 4e3,
    /** Énergie perdue par tick par un court-circuit contaminant. */
    shortCircuitDrain: 3
  },
  maintenance: {
    /** Rustine : rapide et pas chère, le module repart mais reste usé et devient plus fragile. */
    patch: { costShare: 1e3, seconds: 20, secondsPerStage: 5, wearRelief: 1e5 },
    /** Réparation complète : module immobilisé, usure et fragilité remises à zéro. */
    repair: { costShare: 3500, seconds: 90, secondsPerStage: 30 },
    /** Révision d'un module usé mais pas en panne. */
    preventive: { costShare: 2500, seconds: 60 },
    /** Le coût monte avec le palier (dix-millièmes en plus par palier)… */
    stageCost: 5e3,
    /** … et avec chaque minute passée en panne… */
    minuteCost: 500,
    /** … sans dépasser ce multiplicateur pour une réparation… */
    maxCostFactor: 1e5,
    /** … ni celui-ci pour une rustine, qui reste le chemin de retour bon marché. */
    maxPatchCostFactor: 3e4,
    minCost: 5
  },
  /** Part du prix rendue à la démolition d'un module construit (un chantier est remboursé en entier). */
  demolishRefund: 5e3,
  /**
   * Niveau de la station, de 1 à `maxLevel`. Les tableaux sont indexés par niveau (l'indice 0 ne sert pas) ;
   * le niveau de déblocage de chaque module est dans `data/modules.ts`.
   */
  progression: {
    maxLevel: 8,
    /** Expérience cumulée pour atteindre chaque niveau. */
    xpForLevel: [0, 0, 25, 400, 1500, 4500, 12e3, 25e3, 38e3],
    /** Expérience d'un vaisseau reparti entièrement servi. */
    shipXp: { shuttle: 1, freighter: 2, liner: 4 },
    /** Révision d'un module usé avant sa panne : on a vu venir la panne. */
    preventiveXp: 5,
    /** Réparation complète d'un module en panne. */
    repairXp: 3,
    /** Nombre maximal de modules d'un type selon le niveau (chantiers compris) ; les autres types n'ont pas de limite. */
    limits: {
      dock: [0, 2, 3, 4, 5, 6, 7, 8, 9],
      maintenance_bay: [0, 0, 0, 1, 1, 2, 2, 3, 3]
    },
    /** Niveau requis pour ouvrir chaque anneau (indice = anneau), en plus de son prix. */
    ringLevel: [0, 0, 0, 0, 4, 7]
  },
  /** Améliorations des modules, du niveau 1 au niveau de la station (au plus `maxLevel`). */
  upgrades: {
    maxLevel: 8,
    /** Prix pour passer au niveau n : prix de base du module × (n − 1) × cette part (dix-millièmes). */
    costPerLevel: 3e4,
    /** Durée pour passer au niveau n : ce temps × (n − 1). */
    secondsPerLevel: 30,
    /** Production, capacité ou service en plus par niveau au-delà du 1 (dix-millièmes). */
    outputPerLevel: 1500,
    /** Places d'équipage en plus par niveau pour un habitat. */
    habitatCrewPerLevel: 1,
    /** Paiement en plus des vaisseaux qui repartent d'un dock amélioré, par niveau (dix-millièmes). */
    dockPaymentPerLevel: 500,
    /** Interventions plus courtes par niveau de la meilleure baie de maintenance en service (dix-millièmes). */
    baySpeedPerLevel: 500,
    /** Usure en moins par niveau (dix-millièmes). */
    wearReliefPerLevel: 500,
    /** Interventions plus chères par niveau (dix-millièmes). */
    interventionCostPerLevel: 1e3
  },
  offline: {
    /** Au-delà de cette absence, le serveur applique `advanceOffline` au retour. */
    thresholdSeconds: 300,
    /** Gains plafonnés à 8 h d'absence. */
    capSeconds: 8 * 3600,
    /** Régime réduit : part des gains d'une station en service. */
    efficiency: 4500,
    /** L'usure monte au ralenti… */
    wearFactor: 2500,
    /**
     * … sans dépasser ce seuil, juste sous « usé » : une absence ne rend aucun module usé, et ne prépare
     * aucune panne au retour. Seule l'usure du jeu actif pousse ensuite un module vers la zone des pannes.
     */
    wearCeiling: 49e4
  }
};

// src/data/modules.ts
var MODULES = {
  core: {
    type: "core",
    category: "core",
    zone: "core",
    failure: null,
    service: "trade",
    priority: 100,
    unlockLevel: 1
  },
  dock: {
    type: "dock",
    category: "docking",
    zone: "weightless",
    failure: "air_leak",
    service: null,
    priority: 60,
    unlockLevel: 1
  },
  solar_panel: {
    type: "solar_panel",
    category: "energy",
    zone: "any",
    failure: "short_circuit",
    service: null,
    priority: 90,
    unlockLevel: 1
  },
  battery: {
    type: "battery",
    category: "energy",
    zone: "any",
    failure: "overheat",
    service: null,
    priority: 90,
    unlockLevel: 1
  },
  o2_recycler: {
    type: "o2_recycler",
    category: "life",
    zone: "any",
    failure: "air_leak",
    service: null,
    priority: 80,
    unlockLevel: 1
  },
  habitat: {
    type: "habitat",
    category: "living",
    zone: "gravity",
    failure: "air_leak",
    service: null,
    priority: 70,
    unlockLevel: 1
  },
  cantina: {
    type: "cantina",
    category: "service",
    zone: "gravity",
    failure: "air_leak",
    service: "rest",
    priority: 40,
    unlockLevel: 4
  },
  repair_workshop: {
    type: "repair_workshop",
    category: "service",
    zone: "weightless",
    failure: "overheat",
    service: "repair",
    priority: 40,
    unlockLevel: 5
  },
  fuel_depot: {
    type: "fuel_depot",
    category: "service",
    zone: "weightless",
    failure: "overheat",
    service: "fuel",
    priority: 50,
    unlockLevel: 2
  },
  warehouse: {
    type: "warehouse",
    category: "service",
    zone: "weightless",
    failure: "short_circuit",
    service: "trade",
    priority: 40,
    unlockLevel: 1
  },
  maintenance_bay: {
    type: "maintenance_bay",
    category: "maintenance",
    zone: "any",
    failure: "overheat",
    service: null,
    priority: 75,
    unlockLevel: 3
  }
};
var BUILDABLE_MODULES = [
  "dock",
  "solar_panel",
  "battery",
  "o2_recycler",
  "habitat",
  "cantina",
  "fuel_depot",
  "warehouse",
  "repair_workshop",
  "maintenance_bay"
];
function isBuildableModule(type) {
  return typeof type === "string" && BUILDABLE_MODULES.includes(type);
}

// src/progression.ts
var { maxLevel, xpForLevel } = BALANCE.progression;
function levelForXp(xp) {
  let level = 1;
  while (level < maxLevel && xp >= (xpForLevel[level + 1] ?? Infinity)) level += 1;
  return level;
}
function gainXp(state, amount) {
  if (amount <= 0) return;
  state.xp += amount;
  state.level = Math.max(state.level, levelForXp(state.xp));
}
function isUnlocked(level, type) {
  return MODULES[type].unlockLevel <= level;
}
function moduleLimit(level, type) {
  const table = BALANCE.progression.limits[type];
  return table ? table[Math.min(level, maxLevel)] ?? 0 : null;
}
function ringLevel(ring) {
  return BALANCE.progression.ringLevel[ring] ?? 1;
}
function levelInfo(state) {
  const maxed = state.level >= maxLevel;
  return {
    level: state.level,
    xp: state.xp,
    xpFrom: xpForLevel[state.level] ?? 0,
    xpTo: maxed ? null : xpForLevel[state.level + 1] ?? null,
    maxed
  };
}
function levelUnlocks(level) {
  const modules = BUILDABLE_MODULES.filter((type) => MODULES[type].unlockLevel === level);
  const limits = [];
  for (const type of BUILDABLE_MODULES) {
    const from = level > 1 ? moduleLimit(level - 1, type) : 0;
    const to = moduleLimit(level, type);
    if (from !== null && to !== null && to > from) limits.push({ type, from, to });
  }
  const ring = BALANCE.progression.ringLevel.findIndex((needed) => needed === level);
  return { level, modules, limits, ring: ring > 0 ? ring : null };
}

// src/util.ts
var PERMYRIAD = 1e4;
function scale(value, share) {
  return Math.floor(value * share / PERMYRIAD);
}
function clamp(value, min, max) {
  return value < min ? min : value > max ? max : value;
}
function cloneState(state) {
  return JSON.parse(JSON.stringify(state));
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function allocateId(state, prefix) {
  const id = `${prefix}${state.nextId}`;
  state.nextId += 1;
  return id;
}

// src/stats.ts
function perLevel(level, share) {
  return Math.max(0, level - 1) * share;
}
function moduleStats(module2) {
  const base = BALANCE.modules[module2.type];
  if (module2.level <= 1) return base;
  const { outputPerLevel, habitatCrewPerLevel, wearReliefPerLevel } = BALANCE.upgrades;
  const more = PERMYRIAD + perLevel(module2.level, outputPerLevel);
  return {
    ...base,
    power: base.power > 0 ? scale(base.power, more) : base.power,
    crewCapacity: base.crewCapacity + (module2.type === "habitat" ? (module2.level - 1) * habitatCrewPerLevel : 0),
    oxygen: scale(base.oxygen, more),
    oxygenTank: scale(base.oxygenTank, more),
    battery: scale(base.battery, more),
    service: scale(base.service, more),
    wear: scale(base.wear, PERMYRIAD - perLevel(module2.level, wearReliefPerLevel))
  };
}
function dockPaymentFactor(level) {
  return PERMYRIAD + perLevel(level, BALANCE.upgrades.dockPaymentPerLevel);
}
function interventionCostFactor(level) {
  return PERMYRIAD + perLevel(level, BALANCE.upgrades.interventionCostPerLevel);
}
function interventionSpeedFactor(state) {
  let best = 0;
  for (const module2 of state.modules) {
    if (module2.type === "maintenance_bay" && module2.status === "running") best = Math.max(best, module2.level);
  }
  return PERMYRIAD - perLevel(best, BALANCE.upgrades.baySpeedPerLevel);
}

// src/time.ts
var TICK_SECONDS = 1;
function ticks(seconds2) {
  if (seconds2 <= 0) return 0;
  return Math.max(1, Math.floor(seconds2 / TICK_SECONDS));
}
function seconds(tickCount) {
  return tickCount * TICK_SECONDS;
}
var ORBIT_TICKS = ticks(BALANCE.orbit.periodSeconds);
var SHADOW_TICKS = ticks(BALANCE.orbit.shadowSeconds);
var DAY_TICKS = ORBIT_TICKS - SHADOW_TICKS;
function orbitPhase(tick) {
  return (tick % ORBIT_TICKS + ORBIT_TICKS) % ORBIT_TICKS;
}
function isNight(tick) {
  return orbitPhase(tick) >= DAY_TICKS;
}
function orbitInfo(tick) {
  const phase = orbitPhase(tick);
  const night = phase >= DAY_TICKS;
  return {
    night,
    phase,
    ticksToSwitch: night ? ORBIT_TICKS - phase : DAY_TICKS - phase,
    orbitTicks: ORBIT_TICKS,
    shadowTicks: SHADOW_TICKS
  };
}

// src/maintenance.ts
function teamsInfo(state) {
  let total = 0;
  let busy = 0;
  for (const module2 of state.modules) {
    if (module2.type === "core" || module2.status === "running") total += moduleStats(module2).teams;
    if (module2.job && module2.job.kind !== "upgrade") busy += 1;
  }
  return { total, busy, free: Math.max(0, total - busy) };
}
function debtFactor(state, module2, cap = BALANCE.maintenance.maxCostFactor) {
  if (!module2.failure) return PERMYRIAD;
  const { stageCost, minuteCost } = BALANCE.maintenance;
  const minutes = Math.floor(Math.max(0, state.tick - module2.failure.startedAt) / ticks(60));
  return Math.min(cap, PERMYRIAD + module2.failure.stage * stageCost + minutes * minuteCost);
}
function interventionCost(state, module2, share, cap) {
  const base = scale(scale(BALANCE.modules[module2.type].cost, share), interventionCostFactor(module2.level));
  return Math.max(BALANCE.maintenance.minCost, scale(base, debtFactor(state, module2, cap)));
}
function patchCost(state, module2) {
  const { patch, maxPatchCostFactor } = BALANCE.maintenance;
  return interventionCost(state, module2, patch.costShare, maxPatchCostFactor);
}
function repairCost(state, module2) {
  const { repair, preventive, maxCostFactor } = BALANCE.maintenance;
  return interventionCost(state, module2, module2.failure ? repair.costShare : preventive.costShare, maxCostFactor);
}
function interventionTicks(state, seconds2) {
  return Math.max(1, scale(ticks(seconds2), interventionSpeedFactor(state)));
}
function patchTicks(state, module2) {
  const { seconds: seconds2, secondsPerStage } = BALANCE.maintenance.patch;
  return interventionTicks(state, seconds2 + secondsPerStage * (module2.failure?.stage ?? 0));
}
function repairTicks(state, module2) {
  if (!module2.failure) return interventionTicks(state, BALANCE.maintenance.preventive.seconds);
  const { seconds: seconds2, secondsPerStage } = BALANCE.maintenance.repair;
  return interventionTicks(state, seconds2 + secondsPerStage * module2.failure.stage);
}
function upgradeCost(module2) {
  const { costPerLevel } = BALANCE.upgrades;
  return scale(BALANCE.modules[module2.type].cost * module2.level, costPerLevel);
}
function upgradeTicks(state, module2) {
  return interventionTicks(state, BALANCE.upgrades.secondsPerLevel * module2.level);
}
function upgradesSpent(module2) {
  const { costPerLevel } = BALANCE.upgrades;
  const steps = (module2.level - 1) * module2.level / 2;
  return scale(BALANCE.modules[module2.type].cost * steps, costPerLevel);
}
function completeJob(state, module2) {
  const job = module2.job;
  if (!job) return;
  if (job.kind === "patch") {
    module2.failure = null;
    module2.wear = Math.max(0, module2.wear - BALANCE.maintenance.patch.wearRelief);
    module2.patches += 1;
    state.stats.patches += 1;
  } else if (job.kind === "repair") {
    if (module2.failure) gainXp(state, BALANCE.progression.repairXp);
    else if (job.preventive) gainXp(state, BALANCE.progression.preventiveXp);
    module2.failure = null;
    module2.wear = 0;
    module2.patches = 0;
    state.stats.repairs += 1;
  } else {
    module2.level += 1;
    state.stats.upgrades += 1;
  }
  module2.job = null;
}

// src/prices.ts
function ownedCount(state, type) {
  return state.modules.reduce((count, module2) => count + (module2.type === type ? 1 : 0), 0);
}
function priceWithOwned(type, owned) {
  return scale(BALANCE.modules[type].cost, PERMYRIAD + owned * BALANCE.costGrowthPerOwned);
}
function buildPrice(state, type) {
  return priceWithOwned(type, ownedCount(state, type));
}
function demolishRefund(state, module2) {
  if (module2.type === "core") return 0;
  const price = priceWithOwned(module2.type, Math.max(0, ownedCount(state, module2.type) - 1));
  return module2.buildLeft > 0 ? price : scale(price + upgradesSpent(module2), BALANCE.demolishRefund);
}

// src/data/ships.ts
var SHIP_CLASS_DEFS = {
  shuttle: { class: "shuttle", fitsCoreAirlock: true, alwaysNeeds: "trade" },
  freighter: { class: "freighter", fitsCoreAirlock: false, alwaysNeeds: null },
  liner: { class: "liner", fitsCoreAirlock: false, alwaysNeeds: null }
};

// src/rng.ts
function cyrb128(input) {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;
  for (let i = 0; i < input.length; i++) {
    const k = input.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ h1 >>> 18, 597399067);
  h2 = Math.imul(h4 ^ h2 >>> 22, 2869860233);
  h3 = Math.imul(h1 ^ h3 >>> 17, 951274213);
  h4 = Math.imul(h2 ^ h4 >>> 19, 2716044179);
  h1 ^= h2 ^ h3 ^ h4;
  h2 ^= h1;
  h3 ^= h1;
  h4 ^= h1;
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
}
function sfc32(seedA, seedB, seedC, seedD) {
  let a = seedA | 0;
  let b = seedB | 0;
  let c = seedC | 0;
  let d = seedD | 0;
  return () => {
    const t = (a + b | 0) + d | 0;
    d = d + 1 | 0;
    a = b ^ b >>> 9;
    b = c + (c << 3) | 0;
    c = c << 21 | c >>> 11;
    c = c + t | 0;
    return (t >>> 0) / 4294967296;
  };
}
function createRng(...parts) {
  const [a, b, c, d] = cyrb128(parts.join(":"));
  const next = sfc32(a, b, c, d);
  for (let i = 0; i < 12; i++) next();
  const int = (min, max) => min + Math.floor(next() * (max - min + 1));
  const shuffle = (items) => {
    const result = items.slice();
    for (let i = result.length - 1; i > 0; i--) {
      const j = int(0, i);
      const tmp = result[i];
      result[i] = result[j];
      result[j] = tmp;
    }
    return result;
  };
  return {
    next,
    int,
    chancePpm: (perMillion) => perMillion > 0 && int(0, 999999) < perMillion,
    pick: (items) => {
      if (items.length === 0) throw new RangeError("pick() sur une liste vide");
      return items[int(0, items.length - 1)];
    },
    shuffle,
    weighted: (entries) => {
      const total = entries.reduce((sum, [, weight]) => sum + Math.max(0, weight), 0);
      if (total <= 0) throw new RangeError("weighted() sans poids positif");
      let roll = int(0, total - 1);
      for (const [item, weight] of entries) {
        roll -= Math.max(0, weight);
        if (roll < 0) return item;
      }
      return entries[entries.length - 1][0];
    }
  };
}

// src/hash.ts
function canonicalJson(value) {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value) ?? "null";
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => item === void 0 ? "null" : canonicalJson(item)).join(",")}]`;
  }
  const entries = Object.keys(value).sort().filter((key) => value[key] !== void 0).map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`);
  return `{${entries.join(",")}}`;
}
function hashString(input) {
  let h1 = 3735928559;
  let h2 = 1103547991;
  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507);
  h1 ^= Math.imul(h2 ^ h2 >>> 13, 3266489909);
  h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507);
  h2 ^= Math.imul(h1 ^ h1 >>> 13, 3266489909);
  return (h2 >>> 0).toString(16).padStart(8, "0") + (h1 >>> 0).toString(16).padStart(8, "0");
}
function hashState(state) {
  return hashString(canonicalJson(state));
}

// src/grid.ts
function ringOf(x, y) {
  return Math.max(Math.abs(x), Math.abs(y));
}
function zoneOfRing(ring) {
  if (ring === 0) return "core";
  return ring >= BALANCE.grid.gravityFromRing ? "gravity" : "weightless";
}
function zoneAllows(zone, ring) {
  if (ring === 0) return zone === "core";
  if (zone === "any") return true;
  return zone === zoneOfRing(ring);
}
function moduleAllowedOnRing(type, ring) {
  return zoneAllows(MODULES[type].zone, ring);
}
function neighborsOf(x, y) {
  return [
    { x, y: y - 1 },
    { x: x + 1, y },
    { x, y: y + 1 },
    { x: x - 1, y }
  ];
}
function cellKey(x, y) {
  return `${x},${y}`;
}
function moduleGrid(modules) {
  const grid = /* @__PURE__ */ new Map();
  for (const module2 of modules) grid.set(cellKey(module2.x, module2.y), module2);
  return grid;
}
function touchesStation(grid, x, y) {
  return neighborsOf(x, y).some((cell) => grid.has(cellKey(cell.x, cell.y)));
}
function staysConnectedWithout(modules, removedId) {
  const remaining = modules.filter((module2) => module2.id !== removedId);
  const grid = moduleGrid(remaining);
  const core = remaining.find((module2) => module2.type === "core");
  if (!core) return false;
  const seen = /* @__PURE__ */ new Set([cellKey(core.x, core.y)]);
  const queue = [{ x: core.x, y: core.y }];
  while (queue.length > 0) {
    const cell = queue.shift();
    for (const next of neighborsOf(cell.x, cell.y)) {
      const key = cellKey(next.x, next.y);
      if (!seen.has(key) && grid.has(key)) {
        seen.add(key);
        queue.push(next);
      }
    }
  }
  return seen.size === remaining.length;
}
function cellsUpToRing(maxRing) {
  const cells = [];
  for (let ring = 1; ring <= maxRing; ring++) {
    for (let x = -ring; x < ring; x++) cells.push({ x, y: -ring });
    for (let y = -ring; y < ring; y++) cells.push({ x: ring, y });
    for (let x = ring; x > -ring; x--) cells.push({ x, y: ring });
    for (let y = ring; y > -ring; y--) cells.push({ x: -ring, y });
  }
  return cells;
}

// src/step/failures.ts
function contaminating(state, kind) {
  return state.modules.filter((module2) => module2.failure?.kind === kind && module2.failure.stage >= 2);
}
function load(module2, plan, ships) {
  const { idleLoad } = BALANCE.wear;
  if (module2.type === "solar_panel" && plan.night) return 0;
  if (module2.type === "dock") return ships.occupied.has(module2.id) ? PERMYRIAD : idleLoad;
  const service = MODULES[module2.type].service;
  if (service) {
    const pool = ships.pools[service];
    if (pool <= 0) return idleLoad;
    return idleLoad + Math.floor((PERMYRIAD - idleLoad) * ships.used[service] / pool);
  }
  return PERMYRIAD;
}
function applyWear(state, plan, ships) {
  const max = BALANCE.wear.max;
  state.modules.forEach((module2, i) => {
    if (module2.type === "core" || plan.status[i] !== "running") return;
    const rate = scale(moduleStats(module2).wear, module2.wearFactor);
    module2.wear = Math.min(max, module2.wear + scale(rate, load(module2, plan, ships)));
  });
  const overheating = contaminating(state, "overheat");
  if (overheating.length === 0) return;
  const grid = moduleGrid(state.modules);
  for (const source of overheating) {
    for (const cell of neighborsOf(source.x, source.y)) {
      const neighbor = grid.get(cellKey(cell.x, cell.y));
      if (!neighbor || neighbor.type === "core" || neighbor.buildLeft > 0) continue;
      neighbor.wear = Math.min(max, neighbor.wear + BALANCE.wear.overheatNeighbor);
    }
  }
}
function failureChancePpm(module2, night) {
  const { failureFrom, max, failureChancePpm: atMax, nightFactor, fragilityPerPatch } = BALANCE.wear;
  if (module2.type === "core" || module2.wear < failureFrom) return 0;
  let chance = Math.floor(atMax * (module2.wear - failureFrom) / (max - failureFrom));
  if (night) chance = scale(chance, nightFactor);
  return scale(chance, PERMYRIAD + module2.patches * fragilityPerPatch);
}
function updateFailures(state, plan) {
  state.modules.forEach((module2, i) => {
    if (module2.type === "core") return;
    const failure = module2.failure;
    if (failure) {
      if (module2.job || failure.stage >= 3) return;
      if (state.tick - failure.since >= ticks(BALANCE.failures.stageSeconds[failure.stage] ?? 0)) {
        failure.stage = failure.stage + 1;
        failure.since = state.tick;
        state.stats.escalations += 1;
      }
      return;
    }
    if (plan.status[i] !== "running" || module2.job) return;
    const chance = failureChancePpm(module2, plan.night);
    if (chance <= 0) return;
    const forced = module2.wear >= BALANCE.wear.max;
    if (!forced && !createRng(state.seed, "failure", state.tick, module2.id).chancePpm(chance)) return;
    module2.failure = {
      kind: MODULES[module2.type].failure ?? "overheat",
      stage: 1,
      since: state.tick,
      startedAt: state.tick
    };
    state.stats.failures += 1;
  });
}

// src/step/operations.ts
function failureEfficiency(module2) {
  return BALANCE.failures.efficiency[module2.failure?.stage ?? 0] ?? 0;
}
function moraleEfficiency(morale) {
  const { fullEfficiencyFrom, minEfficiency } = BALANCE.morale;
  if (morale >= fullEfficiencyFrom) return PERMYRIAD;
  return minEfficiency + Math.floor((PERMYRIAD - minEfficiency) * Math.max(0, morale) / fullEfficiencyFrom);
}
function moduleEfficiency(state, module2) {
  if (module2.type === "core") return PERMYRIAD;
  const morale = state.morale[ringOf(module2.x, module2.y)] ?? PERMYRIAD;
  return scale(failureEfficiency(module2), moraleEfficiency(morale));
}
function blockingStatus(module2) {
  if (module2.buildLeft > 0) return "building";
  if (module2.job?.kind === "repair") return "repairing";
  if (module2.job?.kind === "upgrade") return "upgrading";
  if (!module2.enabled) return "off";
  if (module2.failure?.stage === 3) return "stopped";
  return null;
}
function planOperations(state, night) {
  const modules = state.modules;
  const status = modules.map(() => "running");
  const eff = modules.map(() => 0);
  const candidates = [];
  let crew = 0;
  let crewNeeded = 0;
  let energyCapacity = 0;
  for (let i = 0; i < modules.length; i++) {
    const module2 = modules[i];
    const blocked = blockingStatus(module2);
    if (blocked) {
      status[i] = blocked;
      continue;
    }
    const balance = moduleStats(module2);
    candidates.push(i);
    crew += balance.crewCapacity;
    crewNeeded += balance.crew;
    energyCapacity += scale(balance.battery, failureEfficiency(module2));
  }
  candidates.sort((a, b) => {
    const pa = MODULES[modules[a].type].priority;
    const pb = MODULES[modules[b].type].priority;
    return pb - pa || a - b;
  });
  let crewLeft = crew;
  const staffed = [];
  for (const i of candidates) {
    const required = moduleStats(modules[i]).crew;
    if (required > crewLeft) {
      status[i] = "unstaffed";
      continue;
    }
    crewLeft -= required;
    staffed.push(i);
  }
  let energyIn = 0;
  for (const i of staffed) {
    const module2 = modules[i];
    const power = moduleStats(module2).power;
    if (power <= 0) continue;
    if (module2.type === "solar_panel" && night) continue;
    energyIn += scale(power, moduleEfficiency(state, module2));
  }
  let budget = state.energy + energyIn;
  let energyOut = 0;
  let energyShortfall = 0;
  for (const i of staffed) {
    const need = -moduleStats(modules[i]).power;
    if (need <= 0) continue;
    if (need > budget) {
      status[i] = "unpowered";
      energyShortfall += need;
      continue;
    }
    budget -= need;
    energyOut += need;
  }
  for (const i of staffed) {
    if (status[i] === "running") eff[i] = moduleEfficiency(state, modules[i]);
  }
  return {
    night,
    status,
    eff,
    crew,
    crewNeeded,
    energyIn,
    energyOut,
    energyCapacity,
    energyAfter: Math.min(energyCapacity, budget),
    energyShortfall
  };
}

// src/step/ships.ts
function emptyNeeds() {
  return { fuel: 0, repair: 0, rest: 0, trade: 0 };
}
function stationCondition(state) {
  let condition = PERMYRIAD;
  for (const module2 of state.modules) {
    if (module2.failure) condition -= BALANCE.failures.condition[module2.failure.stage] ?? 0;
  }
  return Math.max(BALANCE.failures.conditionFloor, condition);
}
function berthModules(state, plan) {
  return state.modules.filter(
    (module2, i) => module2.type === "core" || module2.type === "dock" && plan.status[i] === "running"
  );
}
function servicePools(state, plan) {
  const pools = emptyNeeds();
  state.modules.forEach((module2, i) => {
    const service = MODULES[module2.type].service;
    if (!service || plan.status[i] !== "running") return;
    pools[service] += scale(moduleStats(module2).service, plan.eff[i] ?? 0);
  });
  return pools;
}
function servicesOffered(state) {
  const offered = /* @__PURE__ */ new Set();
  for (const module2 of state.modules) {
    const service = MODULES[module2.type].service;
    if (service && module2.buildLeft === 0) offered.add(service);
  }
  return NEED_KINDS.filter((kind) => offered.has(kind));
}
function shipClassWeights(reputation, condition, services = NEED_KINDS.length, coreAirlock = false) {
  return SHIP_CLASSES.filter(
    (shipClass) => BALANCE.ships.classes[shipClass].minNeeds <= services && (!coreAirlock || SHIP_CLASS_DEFS[shipClass].fitsCoreAirlock)
  ).map((shipClass) => {
    const balance = BALANCE.ships.classes[shipClass];
    let weight = Math.max(1, balance.weight + scale(balance.weightPerReputation, reputation));
    if (shipClass !== "shuttle") weight = Math.max(1, scale(weight, condition));
    return [shipClass, weight];
  });
}
function highwayExitMaxSeconds(reputation) {
  const { exitMin, exitMaxAtZero, exitMaxAtFull } = BALANCE.ships.highway;
  return Math.max(exitMin, exitMaxAtZero - scale(exitMaxAtZero - exitMaxAtFull, reputation));
}
function rollNeeds(rng, shipClass, services) {
  const balance = BALANCE.ships.classes[shipClass];
  const always = SHIP_CLASS_DEFS[shipClass].alwaysNeeds;
  const count = rng.int(balance.minNeeds, Math.min(balance.maxNeeds, services.length));
  const kinds = always && services.includes(always) ? [always] : [];
  for (const kind of rng.shuffle(services)) {
    if (kinds.length >= count) break;
    if (!kinds.includes(kind)) kinds.push(kind);
  }
  const asked = emptyNeeds();
  for (const kind of NEED_KINDS) {
    if (kinds.includes(kind)) asked[kind] = balance.amount * rng.int(75, 125);
  }
  return asked;
}
function callShip(state, berth, condition, services) {
  const rng = createRng(state.seed, "ship", state.tick, berth.id);
  const shipClass = rng.weighted(shipClassWeights(state.reputation, condition, services.length, berth.type === "core"));
  const exit = rng.int(BALANCE.ships.highway.exitMin, highwayExitMaxSeconds(state.reputation));
  state.stats.shipsArrived += 1;
  return {
    id: allocateId(state, "s"),
    class: shipClass,
    phase: "highway",
    phaseLeft: ticks(exit),
    berth: berth.id,
    asked: rollNeeds(rng, shipClass, services),
    served: emptyNeeds(),
    patience: ticks(BALANCE.ships.classes[shipClass].patienceSeconds),
    arrivedAt: state.tick
  };
}
function isFullyServed(ship) {
  return NEED_KINDS.every((kind) => ship.served[kind] >= ship.asked[kind]);
}
function berthLevel(state, ship) {
  const berth = ship.berth ? state.modules.find((module2) => module2.id === ship.berth) : void 0;
  return berth?.type === "dock" ? berth.level : 1;
}
function reputationPaymentFactor(reputation) {
  return PERMYRIAD + scale(BALANCE.ships.reputationBonus, reputation);
}
function shipPayment(state, ship) {
  let value = 0;
  for (const kind of NEED_KINDS) value += Math.min(ship.served[kind], ship.asked[kind]) * BALANCE.ships.prices[kind];
  const tip = isFullyServed(ship) ? BALANCE.ships.tip : 0;
  let factor = scale(BALANCE.ships.classes[ship.class].priceFactor, PERMYRIAD + tip);
  factor = scale(scale(factor, dockPaymentFactor(berthLevel(state, ship))), reputationPaymentFactor(state.reputation));
  return Math.floor(scale(value, factor) / PERMYRIAD);
}
function depart(state, ship) {
  const balance = BALANCE.ships.classes[ship.class];
  const payment = shipPayment(state, ship);
  state.credits += payment;
  state.stats.creditsEarned += payment;
  const anyServed = NEED_KINDS.some((kind) => ship.served[kind] > 0);
  let delta;
  if (isFullyServed(ship)) {
    delta = balance.reputationGain;
    state.stats.shipsServed += 1;
    gainXp(state, BALANCE.progression.shipXp[ship.class]);
  } else if (anyServed) {
    delta = -balance.reputationLoss;
    state.stats.shipsPartial += 1;
  } else {
    delta = -balance.reputationLossEmpty;
    state.stats.shipsUnserved += 1;
  }
  state.reputation = clamp(state.reputation + delta, BALANCE.reputation.floor, BALANCE.reputation.max);
  ship.phase = "leaving";
  ship.phaseLeft = ticks(BALANCE.ships.leaveSeconds);
  ship.berth = null;
}
function turnBack(state, ship) {
  ship.phase = "leaving";
  ship.phaseLeft = ticks(BALANCE.ships.leaveSeconds);
  ship.berth = null;
  state.stats.shipsTurnedBack += 1;
}
function updateShips(state, plan, condition) {
  const berths = berthModules(state, plan);
  const working = new Set(berths.map((module2) => module2.id));
  for (const ship of state.ships) {
    if (ship.phase === "docked") continue;
    ship.phaseLeft -= 1;
    if (ship.phaseLeft > 0) continue;
    if (ship.phase === "highway") {
      ship.phase = "approach";
      ship.phaseLeft = ticks(BALANCE.ships.approachSeconds);
    } else if (ship.phase === "approach") {
      if (ship.berth && working.has(ship.berth)) {
        ship.phase = "docked";
        ship.phaseLeft = ticks(BALANCE.ships.minDockSeconds);
      } else {
        turnBack(state, ship);
      }
    }
  }
  state.ships = state.ships.filter((ship) => !(ship.phase === "leaving" && ship.phaseLeft <= 0));
  const pools = servicePools(state, plan);
  const left = { ...pools };
  for (const ship of state.ships) {
    if (ship.phase !== "docked") continue;
    for (const kind of NEED_KINDS) {
      const missing = ship.asked[kind] - ship.served[kind];
      if (missing <= 0 || left[kind] <= 0) continue;
      const given = Math.min(missing, left[kind], BALANCE.ships.transferPerTick);
      ship.served[kind] += given;
      left[kind] -= given;
    }
  }
  const used = emptyNeeds();
  for (const kind of NEED_KINDS) used[kind] = pools[kind] - left[kind];
  for (const ship of state.ships) {
    if (ship.phase !== "docked") continue;
    if (ship.phaseLeft > 0) ship.phaseLeft -= 1;
    if (isFullyServed(ship)) {
      if (ship.phaseLeft <= 0) depart(state, ship);
      continue;
    }
    ship.patience -= 1;
    if (ship.patience <= 0) depart(state, ship);
  }
  const reserved = /* @__PURE__ */ new Set();
  for (const ship of state.ships) if (ship.berth) reserved.add(ship.berth);
  const free = berths.filter((module2) => !reserved.has(module2.id));
  if (free.length > 0) {
    const services = servicesOffered(state);
    for (const berth of free) state.ships.push(callShip(state, berth, condition, services));
  }
  const occupied = /* @__PURE__ */ new Set();
  for (const ship of state.ships) if (ship.phase === "docked" && ship.berth) occupied.add(ship.berth);
  return { pools, used, occupied, berths: berths.length };
}

// src/step/index.ts
function oxygenBalance(state, plan) {
  let produced = 0;
  let capacity = 0;
  state.modules.forEach((module2, i) => {
    const balance = moduleStats(module2);
    if (module2.buildLeft === 0) capacity += balance.oxygenTank;
    if (plan.status[i] === "running") produced += scale(balance.oxygen, plan.eff[i] ?? 0);
  });
  let consumed = plan.crew * BALANCE.oxygen.perCrew;
  for (const ship of state.ships) {
    if (ship.phase === "docked") consumed += BALANCE.ships.classes[ship.class].crewOxygen;
  }
  consumed += contaminating(state, "air_leak").length * BALANCE.oxygen.airLeakLoss;
  return { produced, consumed, capacity };
}
function updateMorale(state, plan, oxygenShortage) {
  const { base, cantinaBonus, airLeakPenalty, oxygenShortagePenalty, driftPerTick } = BALANCE.morale;
  const cantinas = /* @__PURE__ */ new Set();
  const leaks = /* @__PURE__ */ new Map();
  state.modules.forEach((module2, i) => {
    const ring = ringOf(module2.x, module2.y);
    if (module2.type === "cantina" && plan.status[i] === "running") cantinas.add(ring);
    if (module2.failure?.kind === "air_leak" && module2.failure.stage >= 2) leaks.set(ring, (leaks.get(ring) ?? 0) + 1);
  });
  for (let ring = 1; ring < state.morale.length; ring++) {
    let target = base + (cantinas.has(ring) ? cantinaBonus : 0) - (leaks.get(ring) ?? 0) * airLeakPenalty;
    if (oxygenShortage) target -= oxygenShortagePenalty;
    target = clamp(target, 0, PERMYRIAD);
    const current = state.morale[ring] ?? base;
    const step = clamp(target - current, -driftPerTick, driftPerTick);
    state.morale[ring] = current + step;
  }
}
function updateReputation(state) {
  const every = ticks(BALANCE.reputation.failurePenaltyEverySeconds);
  if (state.tick % every !== 0) return;
  let penalty = 0;
  for (const module2 of state.modules) {
    if (module2.failure) penalty += BALANCE.reputation.failurePenalty[module2.failure.stage] ?? 0;
  }
  if (penalty > 0) {
    state.reputation = clamp(state.reputation - penalty, BALANCE.reputation.floor, BALANCE.reputation.max);
  }
}
function applyPlan(state, plan) {
  state.modules.forEach((module2, i) => {
    module2.status = plan.status[i] ?? "running";
    module2.eff = plan.eff[i] ?? 0;
  });
}
function buildFlow(state, plan, oxygen, ships, condition) {
  return {
    night: plan.night,
    energyIn: plan.energyIn,
    energyOut: plan.energyOut,
    energyCapacity: plan.energyCapacity,
    energyShortfall: plan.energyShortfall,
    oxygenIn: oxygen.produced,
    oxygenOut: oxygen.consumed,
    oxygenCapacity: oxygen.capacity,
    crew: plan.crew,
    crewNeeded: plan.crewNeeded,
    teams: teamsInfo(state).total,
    berths: ships.berths,
    service: { ...ships.pools },
    condition
  };
}
function emptyFlow() {
  return {
    night: false,
    energyIn: 0,
    energyOut: 0,
    energyCapacity: 0,
    energyShortfall: 0,
    oxygenIn: 0,
    oxygenOut: 0,
    oxygenCapacity: 0,
    crew: 0,
    crewNeeded: 0,
    teams: 0,
    berths: 0,
    service: emptyNeeds(),
    condition: PERMYRIAD
  };
}
function refreshDerived(state) {
  const plan = planOperations(state, isNight(state.tick));
  applyPlan(state, plan);
  const condition = stationCondition(state);
  const berths = berthModules(state, plan).length;
  state.flow = buildFlow(state, plan, oxygenBalance(state, plan), { pools: servicePools(state, plan), berths }, condition);
}
function stepInPlace(state) {
  for (const module2 of state.modules) {
    if (module2.buildLeft > 0) module2.buildLeft -= 1;
    if (module2.job && state.tick >= module2.job.endsAt) completeJob(state, module2);
  }
  const plan = planOperations(state, isNight(state.tick));
  applyPlan(state, plan);
  state.energy = plan.energyAfter;
  const shortCircuits = contaminating(state, "short_circuit").length;
  if (shortCircuits > 0) state.energy = Math.max(0, state.energy - shortCircuits * BALANCE.failures.shortCircuitDrain);
  if (plan.energyShortfall > 0) state.stats.energyShortageTicks += 1;
  const oxygen = oxygenBalance(state, plan);
  state.oxygen = clamp(state.oxygen + oxygen.produced - oxygen.consumed, 0, oxygen.capacity);
  const oxygenShortage = state.oxygen === 0 && oxygen.produced < oxygen.consumed;
  if (oxygenShortage) state.stats.oxygenShortageTicks += 1;
  updateMorale(state, plan, oxygenShortage);
  const condition = stationCondition(state);
  const ships = updateShips(state, plan, condition);
  applyWear(state, plan, ships);
  updateFailures(state, plan);
  updateReputation(state);
  state.flow = buildFlow(state, plan, oxygen, ships, condition);
  state.tick += 1;
}

// src/create.ts
var MAX_SEED_LENGTH = 64;
function emptyStats() {
  return {
    creditsEarned: 0,
    creditsSpent: 0,
    shipsArrived: 0,
    shipsServed: 0,
    shipsPartial: 0,
    shipsUnserved: 0,
    shipsTurnedBack: 0,
    failures: 0,
    escalations: 0,
    patches: 0,
    repairs: 0,
    upgrades: 0,
    energyShortageTicks: 0,
    oxygenShortageTicks: 0,
    offlineSeconds: 0
  };
}
function addModule(state, type, x, y, buildLeft) {
  const id = allocateId(state, "m");
  const { min, max } = BALANCE.wear.factorRange;
  const module2 = {
    id,
    type,
    x,
    y,
    level: 1,
    buildLeft,
    enabled: true,
    wear: 0,
    // Les modules ne s'usent pas tous au même rythme : les pannes s'étalent au lieu d'arriver en vague.
    wearFactor: type === "core" ? PERMYRIAD : createRng(state.seed, "module", id).int(min, max),
    patches: 0,
    failure: null,
    job: null,
    status: buildLeft > 0 ? "building" : "running",
    eff: 0
  };
  state.modules.push(module2);
  return module2;
}
function createStation(seed) {
  if (typeof seed !== "string" || seed.length === 0 || seed.length > MAX_SEED_LENGTH) {
    fail("INVALID_SEED", `Graine invalide : 1 \xE0 ${MAX_SEED_LENGTH} caract\xE8res.`);
  }
  const core = BALANCE.modules.core;
  const state = {
    v: SIM_VERSION,
    seed,
    tick: 0,
    credits: BALANCE.start.credits,
    reputation: BALANCE.start.reputation,
    xp: 0,
    level: 1,
    openRings: BALANCE.grid.startRings,
    energy: core.battery,
    oxygen: core.oxygenTank,
    morale: Array.from({ length: BALANCE.grid.maxRing + 1 }, (_, ring) => ring === 0 ? PERMYRIAD : BALANCE.start.morale),
    modules: [],
    ships: [],
    nextId: 1,
    flow: emptyFlow(),
    stats: emptyStats()
  };
  addModule(state, "core", 0, 0, 0);
  addModule(state, "dock", 1, 0, 0);
  addModule(state, "solar_panel", -1, 0, 0);
  for (const module2 of state.modules) module2.wear = BALANCE.startWear[module2.type] ?? 0;
  refreshDerived(state);
  return state;
}

// src/actions.ts
var MAX_COORDINATE = 64;
function isCoordinate(value) {
  return Number.isInteger(value) && Math.abs(value) <= MAX_COORDINATE;
}
function isModuleId(value) {
  return typeof value === "string" && /^m\d{1,9}$/.test(value);
}
function parseAction(raw) {
  if (!isRecord(raw) || typeof raw.type !== "string") fail("INVALID_ACTION", "Action sans type.");
  switch (raw.type) {
    case "BUILD":
      if (!isBuildableModule(raw.module) || !isCoordinate(raw.x) || !isCoordinate(raw.y)) {
        fail("INVALID_ACTION", "Construction invalide.");
      }
      return { type: "BUILD", module: raw.module, x: raw.x, y: raw.y };
    case "DEMOLISH":
    case "PATCH":
    case "REPAIR":
    case "UPGRADE":
      if (!isModuleId(raw.moduleId)) fail("INVALID_ACTION", "Module invalide.");
      return { type: raw.type, moduleId: raw.moduleId };
    case "TOGGLE":
      if (!isModuleId(raw.moduleId) || typeof raw.on !== "boolean") fail("INVALID_ACTION", "Mise en veille invalide.");
      return { type: "TOGGLE", moduleId: raw.moduleId, on: raw.on };
    case "EXTEND_RING":
      return { type: "EXTEND_RING" };
    default:
      return fail("INVALID_ACTION", `Action inconnue : ${raw.type}.`);
  }
}
function findModule(state, moduleId) {
  const module2 = state.modules.find((candidate) => candidate.id === moduleId);
  if (!module2) fail("UNKNOWN_MODULE", "Ce module n'existe pas.", { moduleId });
  return module2;
}
function spend(state, amount) {
  if (state.credits < amount) fail("NOT_ENOUGH_CREDITS", "Cr\xE9dits insuffisants.", { needed: amount, credits: state.credits });
  state.credits -= amount;
  state.stats.creditsSpent += amount;
}
function buildBlocker(state, type, x, y) {
  if (!isUnlocked(state.level, type)) {
    return new SimError("MODULE_LOCKED", "Ce module se d\xE9bloque plus tard.", { level: MODULES[type].unlockLevel });
  }
  const limit = moduleLimit(state.level, type);
  if (limit !== null && ownedCount(state, type) >= limit) {
    return new SimError("MODULE_LIMIT_REACHED", "Limite atteinte pour ce module \xE0 ce niveau.", { limit });
  }
  const ring = ringOf(x, y);
  if (ring === 0 || ring > BALANCE.grid.maxRing) return new SimError("OUT_OF_GRID", "Hors de la grille de la station.");
  if (ring > state.openRings) return new SimError("RING_CLOSED", "Cet anneau n'est pas encore ouvert.", { ring });
  if (!moduleAllowedOnRing(type, ring)) {
    return new SimError("WRONG_ZONE", "Ce module ne peut pas aller sur cet anneau.", { ring, zone: MODULES[type].zone });
  }
  const grid = moduleGrid(state.modules);
  if (grid.has(cellKey(x, y))) return new SimError("CELL_OCCUPIED", "Case d\xE9j\xE0 occup\xE9e.");
  if (!touchesStation(grid, x, y)) return new SimError("NOT_CONNECTED", "Le module doit toucher la station.");
  const price = buildPrice(state, type);
  if (state.credits < price) return new SimError("NOT_ENOUGH_CREDITS", "Cr\xE9dits insuffisants.", { needed: price });
  return null;
}
function assertNotCore(module2) {
  if (module2.type === "core") fail("CORE_LOCKED", "Le noyau ne se touche pas : il fonctionne toujours.");
}
function assertFreeTeam(state) {
  if (teamsInfo(state).free <= 0) fail("NO_FREE_TEAM", "Toutes les \xE9quipes de maintenance sont occup\xE9es.");
}
function upgradeBlocker(state, module2) {
  if (module2.type === "core") return new SimError("CORE_LOCKED", "Le noyau ne se touche pas : il fonctionne toujours.");
  if (module2.level >= BALANCE.upgrades.maxLevel) return new SimError("MAX_LEVEL_REACHED", "Ce module est au niveau maximal.");
  if (module2.level >= state.level) {
    return new SimError("LEVEL_TOO_LOW", "La station doit d'abord monter de niveau.", { level: module2.level + 1 });
  }
  if (module2.buildLeft > 0) return new SimError("UNDER_CONSTRUCTION", "Le module est encore en chantier.");
  if (module2.job) return new SimError("MODULE_BUSY", "Une intervention est d\xE9j\xE0 en cours.");
  if (module2.failure) return new SimError("MODULE_FAILED", "R\xE9pare d'abord ce module.");
  const price = upgradeCost(module2);
  if (state.credits < price) return new SimError("NOT_ENOUGH_CREDITS", "Cr\xE9dits insuffisants.", { needed: price });
  return null;
}
function applyActionInPlace(state, raw) {
  const action = parseAction(raw);
  switch (action.type) {
    case "BUILD": {
      const blocker = buildBlocker(state, action.module, action.x, action.y);
      if (blocker) throw blocker;
      spend(state, buildPrice(state, action.module));
      addModule(state, action.module, action.x, action.y, ticks(BALANCE.modules[action.module].buildSeconds));
      return;
    }
    case "DEMOLISH": {
      const module2 = findModule(state, action.moduleId);
      if (module2.type === "core") fail("CORE_LOCKED", "Le noyau ne se touche pas : il fonctionne toujours.");
      if (state.ships.some((ship) => ship.berth === module2.id && ship.phase === "docked")) {
        fail("DOCK_OCCUPIED", "Un vaisseau est amarr\xE9 \xE0 ce dock.");
      }
      if (!staysConnectedWithout(state.modules, module2.id)) {
        fail("WOULD_DISCONNECT", "Sans ce module, une partie de la station serait isol\xE9e.");
      }
      for (const ship of state.ships) if (ship.berth === module2.id) turnBack(state, ship);
      state.credits += demolishRefund(state, module2);
      state.modules = state.modules.filter((candidate) => candidate.id !== module2.id);
      return;
    }
    case "TOGGLE": {
      const module2 = findModule(state, action.moduleId);
      assertNotCore(module2);
      if (module2.buildLeft > 0) fail("UNDER_CONSTRUCTION", "Le module est encore en chantier.");
      module2.enabled = action.on;
      return;
    }
    case "PATCH": {
      const module2 = findModule(state, action.moduleId);
      assertNotCore(module2);
      if (!module2.failure) fail("NOT_FAILED", "Ce module n'est pas en panne.");
      if (module2.job) fail("MODULE_BUSY", "Une intervention est d\xE9j\xE0 en cours.");
      assertFreeTeam(state);
      spend(state, patchCost(state, module2));
      module2.job = { kind: "patch", startedAt: state.tick, endsAt: state.tick + patchTicks(state, module2), preventive: false };
      return;
    }
    case "REPAIR": {
      const module2 = findModule(state, action.moduleId);
      assertNotCore(module2);
      if (module2.buildLeft > 0) fail("UNDER_CONSTRUCTION", "Le module est encore en chantier.");
      if (module2.job) fail("MODULE_BUSY", "Une intervention est d\xE9j\xE0 en cours.");
      if (!module2.failure && module2.wear === 0) fail("NOTHING_TO_REPAIR", "Ce module est \xE0 neuf.");
      assertFreeTeam(state);
      spend(state, repairCost(state, module2));
      module2.job = {
        kind: "repair",
        startedAt: state.tick,
        endsAt: state.tick + repairTicks(state, module2),
        // Une révision commandée sur un module usé, avant la panne : on l'a vue venir.
        preventive: !module2.failure && module2.wear >= BALANCE.wear.worn
      };
      return;
    }
    case "UPGRADE": {
      const module2 = findModule(state, action.moduleId);
      const blocker = upgradeBlocker(state, module2);
      if (blocker) throw blocker;
      spend(state, upgradeCost(module2));
      module2.job = { kind: "upgrade", startedAt: state.tick, endsAt: state.tick + upgradeTicks(state, module2), preventive: false };
      return;
    }
    case "EXTEND_RING": {
      if (state.openRings >= BALANCE.grid.maxRing) fail("MAX_RING_REACHED", "Tous les anneaux sont ouverts.");
      const needed = ringLevel(state.openRings + 1);
      if (state.level < needed) fail("RING_LOCKED", "La station doit monter de niveau pour ouvrir cet anneau.", { level: needed });
      spend(state, BALANCE.grid.ringCost[state.openRings + 1] ?? 0);
      state.openRings += 1;
      return;
    }
  }
}
function applyAction(state, action) {
  const next = cloneState(state);
  applyActionInPlace(next, action);
  return next;
}

// src/replay.ts
function advance(state, count) {
  const next = cloneState(state);
  for (let i = 0; i < count; i++) stepInPlace(next);
  return next;
}
function replay(start, actions, toTick) {
  if (!Number.isInteger(toTick) || toTick < start.tick) {
    fail("INVALID_TICK", "Le tick vis\xE9 est avant celui de l'\xE9tat de d\xE9part.", { from: start.tick, to: toTick });
  }
  const state = cloneState(start);
  let index = 0;
  while (state.tick < toTick) {
    while (index < actions.length && actions[index].tick === state.tick) {
      try {
        applyActionInPlace(state, actions[index].action);
      } catch (error) {
        if (isSimError(error)) throw new ReplayError(index, error);
        throw error;
      }
      index += 1;
    }
    const next = actions[index];
    if (next && next.tick < state.tick) {
      throw new ReplayError(index, new SimError("ACTION_OUT_OF_ORDER", "Actions hors de l'ordre des ticks."));
    }
    stepInPlace(state);
  }
  if (index < actions.length) {
    throw new ReplayError(
      index,
      new SimError("ACTION_BEYOND_TARGET", "Action dat\xE9e apr\xE8s le tick vis\xE9.", { tick: actions[index].tick })
    );
  }
  return state;
}

// src/offline.ts
function powerCoverage(consumption, dayProduction, nightProduction, battery) {
  if (consumption <= 0) return PERMYRIAD;
  const day = Math.min(PERMYRIAD, Math.floor(dayProduction * PERMYRIAD / consumption));
  const stored = Math.min(battery, Math.max(0, dayProduction - consumption) * DAY_TICKS);
  const nightSupply = nightProduction * SHADOW_TICKS + stored;
  const night = Math.min(PERMYRIAD, Math.floor(nightSupply * PERMYRIAD / (consumption * SHADOW_TICKS)));
  return Math.floor((day * DAY_TICKS + night * SHADOW_TICKS) / ORBIT_TICKS);
}
function needProbabilityMilli(shipClass, kind, services) {
  if (!services.includes(kind)) return 0;
  const { minNeeds, maxNeeds } = BALANCE.ships.classes[shipClass];
  const expectedMilli = (minNeeds + Math.min(maxNeeds, services.length)) * 500;
  const always = SHIP_CLASS_DEFS[shipClass].alwaysNeeds;
  const fixed = always && services.includes(always) ? always : null;
  if (fixed === kind) return 1e3;
  const others = services.length - (fixed ? 1 : 0);
  if (others <= 0) return 0;
  return Math.floor((expectedMilli - (fixed ? 1e3 : 0)) / others);
}
function dockSeconds(shipClass) {
  const { minDockSeconds, transferPerTick } = BALANCE.ships;
  return Math.max(minDockSeconds, Math.ceil(BALANCE.ships.classes[shipClass].amount * 100 / transferPerTick));
}
function spread(berths, coverage, exitSeconds, weights, dockFactor) {
  const total = weights.reduce((sum, [, weight]) => sum + weight, 0);
  if (berths <= 0 || total <= 0) return [];
  const cycle = weights.reduce(
    (sum, [shipClass, weight]) => sum + weight * (exitSeconds + BALANCE.ships.approachSeconds + dockSeconds(shipClass)),
    0
  );
  const shipsMilli = scale(Math.floor(berths * 3600 * 1e3 * total / cycle), coverage);
  return weights.map(([shipClass, weight]) => ({ shipClass, shipsMilli: Math.floor(shipsMilli * weight / total), dockFactor }));
}
function offlineRates(state) {
  const dayPlan = planOperations(state, false);
  const nightPlan = planOperations(state, true);
  const staffed = (i) => dayPlan.status[i] === "running" || dayPlan.status[i] === "unpowered";
  let consumption = 0;
  const dockLevels = [];
  state.modules.forEach((module2, i) => {
    if (!staffed(i)) return;
    consumption += Math.max(0, -moduleStats(module2).power);
    if (module2.type === "dock") dockLevels.push(module2.level);
  });
  const coverage = powerCoverage(consumption, dayPlan.energyIn, nightPlan.energyIn, dayPlan.energyCapacity);
  const capacity = emptyNeeds();
  const hour = ticks(3600);
  state.modules.forEach((module2, i) => {
    const service = MODULES[module2.type].service;
    if (!service || !staffed(i)) return;
    const perTick = scale(moduleStats(module2).service, moduleEfficiency(state, module2));
    capacity[service] += module2.type === "core" ? perTick * hour : scale(perTick * hour, coverage);
  });
  const services = servicesOffered(state);
  const condition = stationCondition(state);
  const exit = Math.floor((BALANCE.ships.highway.exitMin + highwayExitMaxSeconds(state.reputation)) / 2);
  const dockFactor = dockLevels.length > 0 ? Math.floor(dockLevels.reduce((sum, level) => sum + dockPaymentFactor(level), 0) / dockLevels.length) : PERMYRIAD;
  const entries = [
    ...spread(1, PERMYRIAD, exit, shipClassWeights(state.reputation, condition, services.length, true), PERMYRIAD),
    ...spread(dockLevels.length, coverage, exit, shipClassWeights(state.reputation, condition, services.length, false), dockFactor)
  ];
  const perShip = entries.map((entry) => {
    const amount = BALANCE.ships.classes[entry.shipClass].amount * 100;
    const needs = emptyNeeds();
    for (const kind of NEED_KINDS) needs[kind] = Math.floor(needProbabilityMilli(entry.shipClass, kind, services) * amount / 1e3);
    return needs;
  });
  const demand = emptyNeeds();
  entries.forEach((entry, i) => {
    for (const kind of NEED_KINDS) demand[kind] += Math.floor(entry.shipsMilli * perShip[i][kind] / 1e3);
  });
  let share = PERMYRIAD;
  for (const kind of NEED_KINDS) {
    if (demand[kind] > 0) share = Math.min(share, Math.floor(capacity[kind] * PERMYRIAD / demand[kind]));
  }
  let shipsPerHourMilli = 0;
  let valuePerHour = 0;
  let xpPerHourMilli = 0;
  entries.forEach((entry, i) => {
    const ships = scale(entry.shipsMilli, share);
    const balance = BALANCE.ships.classes[entry.shipClass];
    let value = 0;
    for (const kind of NEED_KINDS) {
      value += Math.floor(ships * perShip[i][kind] * BALANCE.ships.prices[kind] / 1e3);
    }
    value = scale(scale(value, balance.priceFactor), PERMYRIAD + BALANCE.ships.tip);
    valuePerHour += scale(scale(value, entry.dockFactor), reputationPaymentFactor(state.reputation));
    shipsPerHourMilli += ships;
    xpPerHourMilli += ships * BALANCE.progression.shipXp[entry.shipClass];
  });
  return { shipsPerHourMilli, valuePerHour, xpPerHourMilli };
}
function advanceOffline(start, elapsedSeconds) {
  const state = cloneState(start);
  const elapsed = Math.max(0, Math.floor(elapsedSeconds));
  const counted = Math.min(elapsed, BALANCE.offline.capSeconds);
  const elapsedTicks = ticks(elapsed);
  const countedTicks = ticks(counted);
  const endTick = state.tick + elapsedTicks;
  const report = {
    elapsedSeconds: elapsed,
    countedSeconds: counted,
    credits: 0,
    shipsServed: 0,
    xp: 0,
    levelFrom: state.level,
    levelTo: state.level,
    completedBuilds: [],
    completedJobs: [],
    watch: []
  };
  for (const module2 of state.modules) {
    if (module2.buildLeft > 0) {
      if (module2.buildLeft <= elapsedTicks) {
        module2.buildLeft = 0;
        report.completedBuilds.push(module2.id);
      } else {
        module2.buildLeft -= elapsedTicks;
      }
    }
    if (module2.job && module2.job.endsAt <= endTick) {
      report.completedJobs.push({ moduleId: module2.id, kind: module2.job.kind });
      completeJob(state, module2);
    }
  }
  for (const ship of state.ships) {
    const payment = shipPayment(state, ship);
    state.credits += payment;
    state.stats.creditsEarned += payment;
    report.credits += payment;
  }
  state.ships = [];
  const rates = offlineRates(state);
  const hour = ticks(3600);
  const efficiency = BALANCE.offline.efficiency;
  const earned = Math.floor(scale(rates.valuePerHour, efficiency) / PERMYRIAD * countedTicks / hour);
  const served = Math.floor(scale(rates.shipsPerHourMilli, efficiency) * countedTicks / hour / 1e3);
  const xp = Math.floor(scale(rates.xpPerHourMilli, efficiency) * countedTicks / hour / 1e3);
  state.credits += earned;
  state.stats.creditsEarned += earned;
  state.stats.shipsServed += served;
  gainXp(state, xp);
  report.credits += earned;
  report.shipsServed = served;
  report.xp = xp;
  const plan = planOperations(state, false);
  const ceiling = BALANCE.offline.wearCeiling;
  state.modules.forEach((module2, i) => {
    if (module2.failure) {
      module2.failure.since += elapsedTicks;
      module2.failure.startedAt += elapsedTicks;
    }
    if (module2.type === "core") return;
    if (plan.status[i] !== "running" && plan.status[i] !== "unpowered") return;
    if (module2.wear >= ceiling) return;
    const rate = scale(moduleStats(module2).wear, module2.wearFactor);
    const added = scale(rate * countedTicks, BALANCE.offline.wearFactor);
    module2.wear = Math.min(ceiling, module2.wear + added);
  });
  state.energy = plan.energyCapacity;
  state.oxygen = state.modules.reduce(
    (sum, module2) => sum + (module2.buildLeft === 0 ? moduleStats(module2).oxygenTank : 0),
    0
  );
  state.tick = endTick;
  state.stats.offlineSeconds += elapsed;
  refreshDerived(state);
  report.levelTo = state.level;
  report.watch = state.modules.filter((module2) => module2.type !== "core" && (module2.failure !== null || module2.wear >= BALANCE.wear.worn)).map((module2) => module2.id);
  return { state, report };
}

// src/migrations/index.ts
var MIGRATIONS = {
  1: migrateV1
};
function migrateV1(state) {
  const stats = { ...state.stats };
  const served = Number(stats.shipsServed ?? 0);
  const repairs = Number(stats.repairs ?? 0);
  const xp = (served + repairs) * 10;
  stats.shipsTurnedBack = 0;
  stats.upgrades = 0;
  const tick = Number(state.tick ?? 0);
  const modules = state.modules.map((module2) => {
    const job = module2.job;
    return {
      ...module2,
      level: 1,
      job: job ? { ...job, startedAt: Math.min(tick, Number(job.endsAt)), preventive: false } : null
    };
  });
  const ships = state.ships.filter((ship) => ship.phase === "docked" || ship.phase === "leaving").map((ship) => ship.phase === "docked" ? { ...ship, phaseLeft: 0 } : ship);
  return { ...state, xp, level: levelForXp(xp), modules, ships, stats };
}
function assertStationShape(value) {
  if (!isRecord(value) || typeof value.v !== "number" || typeof value.seed !== "string" || !Number.isInteger(value.tick) || !Number.isInteger(value.credits) || !Array.isArray(value.modules) || !Array.isArray(value.ships) || !Array.isArray(value.morale) || !isRecord(value.stats) || !isRecord(value.flow)) {
    fail("INVALID_STATE", "\xC9tat de station illisible.");
  }
}
function migrateState(raw, migrations = MIGRATIONS, target = SIM_VERSION) {
  if (!isRecord(raw) || !Number.isInteger(raw.v)) fail("INVALID_STATE", "\xC9tat de station sans version.");
  let state = JSON.parse(JSON.stringify(raw));
  let version = state.v;
  if (version > target) {
    fail("STATE_FROM_FUTURE", "\xC9tat cr\xE9\xE9 par une version plus r\xE9cente de la simulation.", { version, target });
  }
  while (version < target) {
    const migration = migrations[version];
    if (!migration) fail("MISSING_MIGRATION", `Aucune migration depuis la version ${version}.`, { version });
    state = { ...migration(state), v: version + 1 };
    version += 1;
  }
  assertStationShape(state);
  return state;
}

// src/selectors.ts
function wearLevel(wear) {
  if (wear >= BALANCE.wear.critical) return "critical";
  if (wear >= BALANCE.wear.worn) return "worn";
  return "ok";
}
function wearPercent(wear) {
  return Math.floor(wear * 100 / BALANCE.wear.max);
}
function findModule2(state, moduleId) {
  return state.modules.find((module2) => module2.id === moduleId);
}
function buildCheck(state, type, x, y) {
  return buildBlocker(state, type, x, y)?.code ?? null;
}
function upgradeCheck(state, module2) {
  return upgradeBlocker(state, module2)?.code ?? null;
}
function buildableCells(state, type) {
  const grid = moduleGrid(state.modules);
  return cellsUpToRing(state.openRings).filter(
    (cell) => moduleAllowedOnRing(type, ringOf(cell.x, cell.y)) && !grid.has(cellKey(cell.x, cell.y)) && touchesStation(grid, cell.x, cell.y)
  );
}
function nextRingCost(state) {
  if (state.openRings >= BALANCE.grid.maxRing) return null;
  return BALANCE.grid.ringCost[state.openRings + 1] ?? null;
}
function moduleFailureRisk(state, module2) {
  return failureChancePpm(module2, isNight(state.tick));
}
function stationSummary(state) {
  const failures = { 1: 0, 2: 0, 3: 0 };
  for (const module2 of state.modules) if (module2.failure) failures[module2.failure.stage] += 1;
  return {
    tick: state.tick,
    credits: state.credits,
    reputation: state.reputation,
    modules: state.modules.length,
    failures,
    ships: state.ships.length,
    openRings: state.openRings,
    level: state.level,
    xp: state.xp
  };
}
export {
  BALANCE,
  BUILDABLE_MODULES,
  DAY_TICKS,
  FAILURE_KINDS,
  MAX_SEED_LENGTH,
  MIGRATIONS,
  MODULES,
  MODULE_TYPES,
  NEED_KINDS,
  ORBIT_TICKS,
  PERMYRIAD,
  ReplayError,
  SHADOW_TICKS,
  SHIP_CLASSES,
  SHIP_CLASS_DEFS,
  SIM_VERSION,
  SimError,
  TICK_SECONDS,
  advance,
  advanceOffline,
  applyAction,
  applyActionInPlace,
  assertStationShape,
  berthLevel,
  buildBlocker,
  buildCheck,
  buildPrice,
  buildableCells,
  canonicalJson,
  cellsUpToRing,
  cloneState,
  createRng,
  createStation,
  debtFactor,
  demolishRefund,
  dockPaymentFactor,
  failureChancePpm,
  findModule2 as findModule,
  hashState,
  hashString,
  highwayExitMaxSeconds,
  interventionCostFactor,
  interventionSpeedFactor,
  isBuildableModule,
  isFullyServed,
  isNight,
  isReplayError,
  isSimError,
  isUnlocked,
  levelForXp,
  levelInfo,
  levelUnlocks,
  migrateState,
  moduleAllowedOnRing,
  moduleFailureRisk,
  moduleLimit,
  moduleStats,
  neighborsOf,
  nextRingCost,
  orbitInfo,
  orbitPhase,
  ownedCount,
  parseAction,
  patchCost,
  patchTicks,
  priceWithOwned,
  refreshDerived,
  repairCost,
  repairTicks,
  replay,
  reputationPaymentFactor,
  ringLevel,
  ringOf,
  seconds,
  servicesOffered,
  shipClassWeights,
  shipPayment,
  stationCondition,
  stationSummary,
  stepInPlace,
  teamsInfo,
  ticks,
  upgradeBlocker,
  upgradeCheck,
  upgradeCost,
  upgradeTicks,
  upgradesSpent,
  wearLevel,
  wearPercent,
  zoneOfRing
};
//# sourceMappingURL=index.js.map